/**
 * The six switches on the Automations screen. Bounds match the CHECK constraints, so a shop
 * gets a sentence rather than a constraint-violation stack trace.
 */
import { z } from 'zod';
import { idempotency } from './spine';
import { AUTOMATION_MAX_DAYS, AUTOMATION_MIN_DAYS } from '../automations';

const days = z.coerce.number().int().min(AUTOMATION_MIN_DAYS).max(AUTOMATION_MAX_DAYS);

export const automationSettingsInput = idempotency.extend({
  quoteFollowupEnabled: z.boolean().optional(),
  /**
   * At most two, because that is the whole point of a canned automation: a shop that wants
   * six chases wants a different product. Ordered and de-duplicated by the service.
   */
  quoteFollowupOffsetDays: z
    .array(days)
    .min(1)
    .max(2)
    .refine((d) => new Set(d).size === d.length, {
      message: 'Two follow-ups on the same day would send the same email twice',
    })
    .optional(),
  quoteArchiveEnabled: z.boolean().optional(),
  quoteArchiveAfterDays: days.optional(),
  requestArchiveEnabled: z.boolean().optional(),
  requestArchiveAfterDays: days.optional(),
  /** MAP 249 ticket 06: the new automatic emails' switches (Settings → Email). */
  quoteApprovedEnabled: z.boolean().optional(),
  reviewRequestEnabled: z.boolean().optional(),
  serviceReminderEnabled: z.boolean().optional(),
});
export type AutomationSettingsInput = z.infer<typeof automationSettingsInput>;

/**
 * Running the nightly pass by hand. `dryRun` defaults true for the same reason the billing
 * sweep's does: an owner should be able to ask "what would this send tonight" without it
 * having been sent.
 */
export const automationsRunInput = idempotency.extend({
  dryRun: z.boolean().default(true),
});
export type AutomationsRunInput = z.infer<typeof automationsRunInput>;

export const requestArchiveInput = idempotency.extend({
  requestId: z.string().uuid(),
});
export type RequestArchiveInput = z.infer<typeof requestArchiveInput>;
